/* Tracking Calendar View Main Controller
 *
 * Angular Module: trackerbase-app.views.tracking-calendar-view
 * Controller exposed as: calendarView
 */

import angular from "angular";
import kendo from "@progress/kendo-ui";
import "kendo.timezones";

import moment from "moment";

import kendoDirectivesModule from "../../shims/kendoDirectives";

import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";

import uiBootstrap from "angular-ui-bootstrap";

import uiRouter from "@uirouter/angularjs";

import Assert from "tbAssert";
import MessageQueue from "../../models/MessageQueue";

import TrackingCalendarViewModuleConstants from "./Constants";
import TrackingEventEditorModuleConstants from "../tracking-event-editor/Constants";

import uiRouterPreviousStateModule from "tbClient/services/PreviousStateService";

import CurrentTimeZoneServiceModule from "../../services/CurrentTimeZoneService";
import UserInfoServiceModule from "../../services/UserInfoService";
import TrackingCalendarDataServiceModule from "../../services/TrackingCalendarDataService";
import TrackingCalendarOptionsServiceModule from "../../services/TrackingCalendarOptionsService";
import EventFilterStoreServiceModule from "../../services/EventFilterStoreService";
import CalendarEventIdFormatFilterModule from "../../filters/CalendarEventIdFormatFilter";
import CalendarGlobalViewServiceModule from "tbClient/services/CalendarGlobalViewService";

import CalendarFilterPanelComponentModule from "./CalendarFilterPanel";
import CalendarOptionsPanelComponentModule from "./CalendarOptionsPanel";
import CalendarEventInfoPanelComponentModule from "./CalendarEventInfoPanel";
import TrackingCalendarEventComponentsModule from "./calendar-event";
import TimeZoneControlPanelComponentModule from "./TimeZoneControlPanel";
import TbAppConstants from "../../config/tbAppConstants";
import NormalizeCalendarParameters from "./NormalizeCalendarParameters";

const trackingCalendarViewRootElementId = "tbTrackingCalendarViewRoot";

const mScopeSvc = new WeakMap();
const mRootScopeSvc = new WeakMap();
const mTimeoutSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mAnchorScrollSvc = new WeakMap();
const mFilterSvc = new WeakMap();
const mDocumentSvc = new WeakMap();
const mWindowSvc = new WeakMap();
const mUIRouterStateSvc = new WeakMap();
const mUIRouterStateParams = new WeakMap();
const mUIRouterPreviousStateSvc = new WeakMap();
const mUIRouterTransitionMgrSvc = new WeakMap();
const mUIRouterTransitionSvc = new WeakMap();
const mCalendarParams = new WeakMap();
const mTrackingCalendarDataSvc = new WeakMap();
const mCurrentTimeZoneSvc = new WeakMap();
const mUserInfoSvc = new WeakMap();
const mTrackingCalendarOptionsSvc = new WeakMap();
const mEventFilterStoreSvc = new WeakMap();
const mCalendarEventIdFormatFilter = new WeakMap();
const mCalendarGlobalViewSvc = new WeakMap();

const errorMessageType = "danger";
const mErrorMessageQueue = new WeakMap();

const mEventInfoPanelIsVisible = new WeakMap();

const mCalendarOptionsMenuIsOpen = new WeakMap();
const mUserShowFilterPanel = new WeakMap();
const mFilterPanelInitialized = new WeakMap();
const mUserShowTimeZonePanel = new WeakMap();
const mTimeZonePanelInitialized = new WeakMap();
const mUserShowCalendarOptionsPanel = new WeakMap();
const mCalendarOptionsPanelInitialized = new WeakMap();

const mCurrentEventId = new WeakMap();
const mCalendarTargetDate = new WeakMap();
const mCurrentTimeZone = new WeakMap();
const mCurrentView = new WeakMap();

const mSchedulerOptions = new WeakMap();

const CalendarViews = Object.freeze({
    Agenda: "agenda",
    Week: "week",
    Month: "month",
    ThreeDay: "threeday",
    Day: "day"
});

const availableCalendarViews = new Set([
    CalendarViews.Agenda,
    CalendarViews.Week,
    CalendarViews.Month,
    CalendarViews.ThreeDay,
    CalendarViews.Day
]);

const altThreeDayTitle = "3 Day";

const viewTitleToViewNameMap = new Map([
    //Add the built-in views, which map to themselves.
    [CalendarViews.Agenda, CalendarViews.Agenda],
    [CalendarViews.Week, CalendarViews.Week],
    [CalendarViews.Month, CalendarViews.Month],
    [CalendarViews.Day, CalendarViews.Day],
    //Map three day view title to internal name, as well as self to self.
    [CalendarViews.ThreeDay, CalendarViews.ThreeDay],
    [altThreeDayTitle, CalendarViews.ThreeDay]
]);

const viewNametoViewTitleMap = new Map([
    //Add the built-in views, which map to themselves.
    [CalendarViews.Agenda, new Set([CalendarViews.Agenda])],
    [CalendarViews.Week, new Set([CalendarViews.Week])],
    [CalendarViews.Month, new Set([CalendarViews.Month])],
    [CalendarViews.Day, new Set([CalendarViews.Day])],
    //Map three day view name to self and alternate names.
    [
        CalendarViews.ThreeDay, new Set([
            CalendarViews.ThreeDay,
            altThreeDayTitle
        ])
    ]
]);

const tbCalendarThreeDayView = kendo.ui.MultiDayView.extend({
    nextDate: function() {
        return kendo.date.nextDay(this.startDate());
    },
    options: {
        selectedDateFormat: "{0:D} - {1:D}"
    },
    name: CalendarViews.ThreeDay,
    calculateDateRange: function() {
        let viewDate = this.options.date;
        let viewRange = 3;
        let viewDates = [];

        for (let idx = 0; idx < viewRange; idx++) {
            viewDates.push(viewDate);
            viewDate = kendo.date.nextDay(viewDate);
        }

        this._render(viewDates);
    }
});

function createAgendaView(numDays, logSvc) {

    if ((!Number.isInteger(numDays)) || (numDays < 1) || (numDays > 7)) {
        logSvc.error("Invalid Number of Days Value Building Agenda View.");
        numDays = 1;
    }

    let agendaViewSelectedDateFormat = "{0:D} - {1:D}";

    if (numDays === 1) {
        agendaViewSelectedDateFormat = "{0:D}";
    }

    let calendarAgendaView = kendo.ui.AgendaView.extend({
        endDate: function() {
            let date = kendo.ui.AgendaView.fn.startDate.call(this);
            return kendo.date.addDays(date, numDays - 1);
        },
        options: {
            selectedDateFormat: agendaViewSelectedDateFormat
        }
    });

    return calendarAgendaView;
}

//Note: dateFormatFn is $filter("date")
function buildPageTitle(currentViewName, currentTargetDate, dateFormatFn) {

    let pageTitle = "TrackerBase: ";
    let knownCurrentView = false;
    let dateFormat = "";

    switch (currentViewName) {
        case CalendarViews.Day:
            dateFormat = "MMMM d, yyyy";
            knownCurrentView = true;
            break;
        case CalendarViews.ThreeDay:
            dateFormat = "'3-Day View: 'MMMM d, yyyy";
            knownCurrentView = true;
            break;
        case CalendarViews.Week:
            dateFormat = "'Week of 'MMMM d, yyyy";
            knownCurrentView = true;
            break;
        case CalendarViews.Month:
            dateFormat = "MMMM yyyy";
            knownCurrentView = true;
            break;
        case CalendarViews.Agenda:
            dateFormat = "'Agenda: 'MMMM d, yyyy";
            knownCurrentView = true;
            break;
        default:
            pageTitle = pageTitle + "Tracking Calendar";
            knownCurrentView = false;
            break;
    }

    if (knownCurrentView === true) {
        pageTitle = pageTitle + dateFormatFn(
                currentTargetDate,
                dateFormat
            );
    }

    return pageTitle;

}

function isCurrentView(queryingViewName, currentViewTitle) {

    let viewTitleSet = viewNametoViewTitleMap.get(queryingViewName);
    let currentViewTitleInViewTitleSet = viewTitleSet.has(currentViewTitle);
    return currentViewTitleInViewTitleSet;
}

class TrackingCalendarViewController {
    constructor(
        $scope,
        $rootScope,
        $timeout,
        $log,
        $anchorScroll,
        $filter,
        $document,
        $window,
        $templateRequest,
        $state,
        $transitions,
        $transition$,
        $previousState,
        calendarParams,
        TrackingCalendarData,
        CurrentTimeZone,
        UserInfoService,
        TrackingCalendarOptions,
        EventFilterStore,
        tbCalendarEventIdFormatFilter,
        CalendarGlobalViewService)
    {

        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($rootScope != null, "Assertion Failure: Missing Root Scope Service Dependency");
        Assert.StrictAssert($timeout != null, "Assertion Failure: Missing Timeout Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($anchorScroll != null, "Assertion Failure: Missing Anchor Scroll Service Dependency");
        Assert.StrictAssert($filter != null, "Assertion Failure: Missing Filter Service Dependency");
        Assert.StrictAssert($document != null, "Assertion Failure: Missing Document Service Dependency");
        Assert.StrictAssert($window != null, "Assertion Failure: Missing Window Service Dependency");
        Assert.StrictAssert($templateRequest != null, "Assertion Failure: Missing Template Request Service Dependency");
        Assert.StrictAssert($state != null, "Assertion Failure: Missing UI Router State Service Dependency");
        Assert.StrictAssert($transitions != null, "Assertion Failure: Missing UIRouter Transitions Service Dependency");
        Assert.StrictAssert($transition$ != null, "Assertion Failure: Missing UI Router Transition Object Dependency");
        Assert.StrictAssert($previousState != null, "Assertion Failure: Missing UI Router Previous State Service Dependency");
        Assert.StrictAssert(calendarParams != null, "Assertion Failure: Missing Calendar Parameters Input Dependency");
        Assert.StrictAssert(TrackingCalendarData != null, "Assertion Failure: Missing Tracking Calendar Service Dependency");
        Assert.StrictAssert(CurrentTimeZone != null, "Assertion Failure: Missing Current Time Zone  Service Dependency");
        Assert.StrictAssert(UserInfoService != null, "Assertion Failure: Missing User Info Service Dependency");
        Assert.StrictAssert(TrackingCalendarOptions != null, "Assertion Failure: Missing Tracking Calendar Options Service Dependency");
        Assert.StrictAssert(EventFilterStore != null, "Assertion Failure: Missing Event Filter Store Service Dependency");
        Assert.StrictAssert(CalendarGlobalViewService != null, "Assertion Failure: Missing Calendar Global View Service Dependency");
        Assert.StrictAssert(tbCalendarEventIdFormatFilter != null, "Assertion Failure: Missing Calendar Event Id Format Filter Dependency");

        mScopeSvc.set(this, $scope);
        mRootScopeSvc.set(this, $rootScope);
        mTimeoutSvc.set(this, $timeout);
        mLogSvc.set(this, $log);
        mAnchorScrollSvc.set(this, $anchorScroll);
        mFilterSvc.set(this, $filter);
        mDocumentSvc.set(this, $document);
        mWindowSvc.set(this, $window);
        mUIRouterStateSvc.set(this, $state);
        mUIRouterTransitionMgrSvc.set(this, $transitions);
        mUIRouterTransitionSvc.set(this, $transition$);
        mUIRouterStateParams.set(this, $transition$.params());

        mUIRouterPreviousStateSvc.set(this, $previousState);
        mCalendarParams.set(this, calendarParams);
        mTrackingCalendarDataSvc.set(this, TrackingCalendarData);
        mCurrentTimeZoneSvc.set(this, CurrentTimeZone);
        mUserInfoSvc.set(this, UserInfoService);
        mTrackingCalendarOptionsSvc.set(this, TrackingCalendarOptions);
        mEventFilterStoreSvc.set(this, EventFilterStore);
        mCalendarEventIdFormatFilter.set(this, tbCalendarEventIdFormatFilter);
        mCalendarGlobalViewSvc.set(this, CalendarGlobalViewService);

        mErrorMessageQueue.set(this, new MessageQueue(errorMessageType, null));

        mEventInfoPanelIsVisible.set(this, false);

        mCalendarOptionsMenuIsOpen.set(this, false);
        mUserShowFilterPanel.set(this, false);
        mFilterPanelInitialized.set(this, false);
        mUserShowTimeZonePanel.set(this, false);
        mTimeZonePanelInitialized.set(this, false);
        mUserShowCalendarOptionsPanel.set(this, false);
        mCalendarOptionsPanelInitialized.set(this, false);

        mCurrentEventId.set(this, null);

        // Scheduler widget is k-ng-delayed on schedulerOptions, so we set to undefined prevent initialization of the widget.
        mSchedulerOptions.set(this, undefined);
    }

    _extractCalendarDateAndViewFromCalendarParams() {

        const logSvc = mLogSvc.get(this);
        const errorMessageQueue = mErrorMessageQueue.get(this);
        const currentCalendarParams = mCalendarParams.get(this);

        logSvc.debug("Calendar Params:");
        logSvc.debug(currentCalendarParams);

        if (angular.isString(currentCalendarParams.errorMessage)) {
            errorMessageQueue.publishMessage(currentCalendarParams.errorMessage);
        }

        let chosenDate = new Date();
        let defaultChosenView = mTrackingCalendarOptionsSvc.get(this).DefaultCalendarView;

        if ((currentCalendarParams.year != null) &&
            (currentCalendarParams.month != null))
        {

            let cYear = parseInt(currentCalendarParams.year, 10);
            let cMonth = parseInt(currentCalendarParams.month, 10);

            if ((Number.isInteger(cYear)) && (Number.isInteger(cMonth))) {
                cMonth = cMonth - 1;

                if (currentCalendarParams.day != null) {
                    let cDay = parseInt(currentCalendarParams.day, 10);
                    if (Number.isInteger(cDay)) {
                        defaultChosenView = "day";
                        chosenDate = new Date(cYear, cMonth, cDay);
                    }
                    else {
                        cDay = 1;
                    }
                }
                else {
                    chosenDate = new Date(cYear, cMonth, 1);
                    defaultChosenView = "month";
                }
            }
        }

        let chosenView = null;

        if (availableCalendarViews.has(currentCalendarParams.calendarViewType)) {
            chosenView = currentCalendarParams.calendarViewType;
        }
        //Redundant with final else, but reserved for future change in behavior.
        else if (currentCalendarParams.calendarViewType === "default") {
            chosenView = defaultChosenView;
        }
        else {
            chosenView = defaultChosenView;
        }

        return {
            chosenView: chosenView,
            chosenDate: chosenDate
        };
    }

    $onInit() {
        const logSvc = mLogSvc.get(this);
        const scopeSvc = mScopeSvc.get(this);
        const globalViewSvc = mCalendarGlobalViewSvc.get(this);

        const anchorScrollOffsetTarget = `#${TbAppConstants.mainNavBarElementId}`;
        mAnchorScrollSvc.get(this).yOffset = angular.element(anchorScrollOffsetTarget);

        let tbCalendarAgendaView = createAgendaView(
            mTrackingCalendarOptionsSvc.get(this).AgendaDaysToShowPref,
            logSvc
        );

        let chosenView, chosenDate;
        ({ chosenView, chosenDate } = this._extractCalendarDateAndViewFromCalendarParams());

        mCalendarTargetDate.set(this, chosenDate);

        mCurrentTimeZone.set(this, mCurrentTimeZoneSvc.get(this).get());
        logSvc.debug("Using time zone:");
        logSvc.debug(mCurrentTimeZone.get(this));

        mCurrentView.set(this, chosenView);

        this.setCurrentPageTitle();
        this.replacePageUrl(mCalendarTargetDate.get(this), mCurrentView.get(this));

        let schedulerDataSourceParams = {
            batch: false,
            serverFiltering: true,
            schema: {
                timezone: mCurrentTimeZone.get(this),
                model: {
                    id: "trackingEventId",
                    fields: {
                        trackingEventId: {
                            from: "trackingEventId",
                            type: "number"
                        },
                        title: {
                            from: "title"
                        },
                        displayTitle: {
                            from: "displayTitle",
                            editable: false
                        },
                        start: {
                            from: "start",
                            type: "date"
                        },
                        startTimezone: {
                            from: "eventTimezone"
                        },
                        end: {
                            from: "end",
                            type: "date"
                        },
                        endTimezone: { // scheduler event requires start/end tz.
                            from: "eventTimezone"
                        },
                        recurrenceId: { from: "recurrenceID" },
                        recurrenceRule: { from: "recurrenceRule" },
                        recurrenceException: { from: "recurrenceException" },
                        isAllDay: { type: "boolean", from: "isAllDay" },
                        isInternal: {
                            from: "isInternal",
                            type: "boolean",
                            nullable: false,
                            editable: false
                        },
                        isExternal: {
                            from: "isExternal",
                            type: "boolean",
                            nullable: false,
                            editable: false
                        },
                        statusName: {
                            from: "statusName",
                            editable: false
                        },
                        locationName: {
                            from: "locationName",
                            editable: true,
                            defaultValue: "",
                            validation: { required: true }
                        },
                        locationAddress: {
                            from: "locationAddress",
                            editable: true,
                            validation: { required: true }
                        },
                        locationCity: {
                            from: "locationCity",
                            editable: true,
                            validation: { required: true }
                        },
                        locationStateId: {
                            from: "locationStateId",
                            type: "number",
                            editable: true,
                            validation: { required: true, min: 1 }
                        },
                        locationStateName: {
                            from: "locationStateName",
                            editable: false
                        },
                        description: {
                            from: "description",
                            nullable: true
                        },
                        notes: {
                            from: "notes",
                            nullable: true
                        },
                        pressDetails: {
                            from : "pressDetails",
                            nullable: true
                        },
                        availableTransitions: {
                            from: "availableTransitions",
                            editable: false,
                            nullable: false
                        },
                        eventTypes: {
                            from: "eventTypes",
                            editable: false,
                            nullable: false
                        },
                        eventTrackers: {
                            from: "eventTrackers",
                            editable: false,
                            nullable: false
                        },
                        eventAttendees: {
                            from: "eventAttendees",
                            editable: false,
                            nullable: false
                        }
                    }
                }
            },
            transport: {
                read: (options) => {
                    let targetDate = mCalendarTargetDate.get(this);
                    let currentTZ = mCurrentTimeZone.get(this);
                    let calendarView = mCurrentView.get(this);

                    mTrackingCalendarDataSvc.get(this)
                                            .queryCalendar(
                                                options,
                                                calendarView,
                                                targetDate,
                                                currentTZ
                                            )
                                            .then(
                                                (response) => {
                                                    options.success(response);
                                                },
                                                (errorResponse) => {
                                                    options.error(errorResponse);
                                                }
                                            );
                },
                create: false,
                update: (options) => {
                    mTrackingCalendarDataSvc.get(this)
                                            .updateEventFromCalendar(options)
                                            .then(
                                                (response) => {
                                                    options.success(response);
                                                },
                                                (errorResponse) => {
                                                    options.error(errorResponse);
                                                }
                                            );
                },
                destroy: false
            }
        };

        let schedulerOptionsData = {
            data: null, //will be set in post-template callback.
            date: mCalendarTargetDate.get(this),
            min: new Date(1900, 0, 1), //Set minimum date as January 1, 1900.
            timezone: mCurrentTimeZone.get(this),
            editable: {
                create: false,
                move: false, //disable dragging events to change their time
                resize: false,
                update: false,
                confirmation: false,
                destroy: false
            },
            edit: function(e) {
                e.preventDefault();
            },
            pdf: {
                author: mUserInfoSvc.get(this).userName,
                creator: "TrackerBase",
                margin: {
                    top: "0.5in",
                    bottom: "0.5in",
                    left: "0.5in",
                    right: "0.5in"
                }
            },
            allDaySlot: true,
            showWorkHours: true,
            workDayStart: new Date("2011/1/1 6:00 AM"),
            workDayEnd: new Date("2011/1/2 12:00 AM"),
            views: [
                {
                    type: "day",
                    selected: isCurrentView(CalendarViews.Day, mCurrentView.get(this))
                },
                {
                    type: tbCalendarThreeDayView,
                    selected: isCurrentView(CalendarViews.ThreeDay, mCurrentView.get(this)),
                    title: "3 Day"
                },
                {
                    type: "week",
                    selected: isCurrentView(CalendarViews.Week, mCurrentView.get(this))
                },
                {
                    type: "month",
                    selected: isCurrentView(CalendarViews.Month, mCurrentView.get(this))
                },
                {
                    type: tbCalendarAgendaView,
                    selected: isCurrentView(CalendarViews.Agenda, mCurrentView.get(this)),
                    title: "agenda"
                }
            ]
        };

        logSvc.debug("Requesting Event Template");
        mTimeoutSvc.get(this)(() => TrackingCalendarViewController.childTemplates.schedulerEventTemplate, 5)
            .then((evtTemplate) => {
                logSvc.debug("Recieved Event Template");
                schedulerOptionsData.allDayEventTemplate = evtTemplate;
                schedulerOptionsData.eventTemplate = evtTemplate;

                logSvc.debug("Initializing Calendar Data Source");
                let calendarEventFilter = mEventFilterStoreSvc.get(this).calendarEventFilter;
                Assert.StrictAssert(
                    calendarEventFilter != null,
                    "Calendar Initialization: Calendar Filter Not Ready"
                );

                this._notifyFilterApplied({ calendarEventFilter: calendarEventFilter });
                const currentGlobalView = globalViewSvc.getCurrentView();
                schedulerDataSourceParams.filter = calendarEventFilter.dataSourceFilter(currentGlobalView.rule);
                const schedulerDataSource = new kendo.data.SchedulerDataSource(schedulerDataSourceParams);
                schedulerOptionsData.data = schedulerDataSource;
                mSchedulerOptions.set(this, schedulerOptionsData); // will trigger initialization of the scheduler widget.

                scopeSvc.$on("kendoWidgetCreated", (e, widget) => {
                    if (widget === scopeSvc.trackingScheduler) {

                        // $log.debug("Kendo Scheduler Widget created");
                        widget.setDataSource(schedulerDataSource);
                    }
                });
            });

        let directLinkId = mCalendarParams.get(this).selectedEventId;
        if ((Number.isInteger(directLinkId)) &&
            (directLinkId > 0)) {

            logSvc.debug("Selected Event Was In Calendar Params");
            this.handleDirectEventLink(directLinkId);
        }

        scopeSvc.$watch(
            () => this.eventInfoPanelIsVisible,
            (newVal, oldVal) => {
                if (newVal !== oldVal) {
                    this._refreshScheduler(505);
                }
            }
        );

        scopeSvc.$watch(
            () => globalViewSvc.getCurrentView(),
            (newGlobalView, oldGlobalView) => {
                if (newGlobalView != null && (oldGlobalView == null || newGlobalView.name !== oldGlobalView.name)) {
                    this.applyTrackingCalendarFilter();
                }
            }
        );

        mLogSvc.get(this).debug("Adding Calendar-Url Sync Transition Success Hook.");
        const removeUrlChangeWatchHook =
            mUIRouterTransitionMgrSvc.get(this)
                .onSuccess({ to: TrackingCalendarViewModuleConstants.rootStateName}, (transition) => {
                    let changedParams = transition._changedParams();
                    if ((transition.options().source === "url") && changedParams && changedParams.length > 0) {
                        logSvc.debug("Finished state change started by change in url. Syncing calendar.", {
                            changes: changedParams
                        });
                        this._syncCalendarToCurrentStateParams(transition.params());
                    }
        });

        scopeSvc.$on(
            "$destroy",
            () => {
                //Remove the url change handler hook
                mLogSvc.get(this).debug("Removing Calendar-Url Sync Transition Success Hook.");
                removeUrlChangeWatchHook();

                //remove all manually set events on the window.
                // Currently None

                //prepare all kendo widgets for imminent destruction.
                let calendarRootElement = angular.element(`#${trackingCalendarViewRootElementId}`);
                kendo.destroy(calendarRootElement);
            }
        );

    }

    get calendarErrorMessages() {
        return mErrorMessageQueue.get(this).messages;
    }

    get eventInfoPanelIsVisible() {
        return mEventInfoPanelIsVisible.get(this);
    }

    set eventInfoPanelIsVisible(updatedVal) {
        mEventInfoPanelIsVisible.set(this, updatedVal);
    }

    get calendarOptionsMenuIsOpen() {
        return mCalendarOptionsMenuIsOpen.get(this);
    }

    set calendarOptionsMenuIsOpen(updatedVal) {
        mCalendarOptionsMenuIsOpen.set(this, updatedVal);
    }

    get userShowFilterPanel() {
        return mUserShowFilterPanel.get(this);
    }

    set userShowFilterPanel(updatedVal) {
        mUserShowFilterPanel.set(this, updatedVal);
    }

    get filterPanelInitialized() {
        return mFilterPanelInitialized.get(this);
    }

    set filterPanelInitialized(updatedVal) {
        mFilterPanelInitialized.set(this, updatedVal);
    }

    get userShowTimeZonePanel() {
        return mUserShowTimeZonePanel.get(this);
    }

    set userShowTimeZonePanel(updatedVal) {
        mUserShowTimeZonePanel.set(this, updatedVal);
    }

    get timeZonePanelInitialized() {
        return mTimeZonePanelInitialized.get(this);
    }

    set timeZonePanelInitialized(updatedVal) {
        mTimeZonePanelInitialized.set(this, updatedVal);
    }

    get userShowCalendarOptionsPanel() {
        return mUserShowCalendarOptionsPanel.get(this);
    }

    set userShowCalendarOptionsPanel(updatedVal) {
        mUserShowCalendarOptionsPanel.set(this, updatedVal);
    }

    get calendarOptionsPanelInitialized() {
        return mCalendarOptionsPanelInitialized.get(this);
    }

    set calendarOptionsPanelInitialized(updatedVal) {
        mCalendarOptionsPanelInitialized.set(this, updatedVal);
    }

    get currentEventId() {
        return mCurrentEventId.get(this);
    }

    get currentView() {
        return mCurrentView.get(this);
    }

    get schedulerOptions() {
        return mSchedulerOptions.get(this);
    }


    // Methods
    _refreshScheduler(delay) {
        if (mScopeSvc.get(this).trackingScheduler != null) {

            mTimeoutSvc.get(this)(() => {
                mScopeSvc.get(this).trackingScheduler.refresh();
            }, delay);
        }
    }

    _syncCalendarToCurrentStateParams(updatedParameters) {
        const currentStateParams = updatedParameters;
        const normalizedCalendarParams = NormalizeCalendarParameters(currentStateParams);

        mLogSvc.get(this).debug("CurrentStateParamsSync: Reading Parameters.", {
            stateParams: currentStateParams,
            normalizedParams: normalizedCalendarParams
        });

        mUIRouterStateParams.set(this, currentStateParams);
        mCalendarParams.set(this, normalizedCalendarParams);

        let chosenDate, chosenView;
        ({chosenDate, chosenView} = this._extractCalendarDateAndViewFromCalendarParams());

        mLogSvc.get(this).debug("CurrentStateParamsSync: Extracted Parameters.", {
            extractedDate: chosenDate,
            extractedView: chosenView
        });

        const theScheduler = mScopeSvc.get(this).trackingScheduler;

        if (!theScheduler) {
            return;
        }

        const updateDate = (chosenDate !== theScheduler.date());
        const updateView = (chosenView !== theScheduler.viewName());

        if (updateDate) {
            mCalendarTargetDate.set(this, chosenDate);
            theScheduler.date(chosenDate);
        }
        if (updateView) {
            mCurrentView.set(this, chosenView);
            theScheduler.view(chosenView);
        }
    }

    removeErrorMessage(msgIdx) {
        mErrorMessageQueue.get(this).removeMessageAtIdx(msgIdx);
    }

    clearErrorMessages() {
        mErrorMessageQueue.get(this).clearMessages();
    }

    hasErrorMessages() {
        return (mErrorMessageQueue.get(this).messageCount > 0);
    }

    toggleCalendarOptionsMenu(e) {
        if (e != null) {
            e.preventDefault();
        }

        mCalendarOptionsMenuIsOpen.set(this, !this.calendarOptionsMenuIsOpen);
    }

    showTimeZonePanel(e) {
        if (e != null) {
            e.preventDefault();
        }

        mCalendarOptionsMenuIsOpen.set(this, false);

        if (!this.timeZonePanelInitialized) {
            mTimeZonePanelInitialized.set(this, true);
            mTimeoutSvc.get(this)( () => {
                mUserShowTimeZonePanel.set(this, true);
            }, 50);
        }
        else {
            mUserShowTimeZonePanel.set(this, true);
        }
    }

    showCalendarOptionsPanel(e) {
        if (e != null) {
            e.preventDefault();
        }

        mCalendarOptionsMenuIsOpen.set(this, false);

        if (!this.calendarOptionsPanelInitialized) {
            mCalendarOptionsPanelInitialized.set(this, true);
            mTimeoutSvc.get(this)(() => {
                mUserShowCalendarOptionsPanel.set(this, true);
            }, 50);
        }
        else {
            mUserShowCalendarOptionsPanel.set(this, true);
        }
    }

    showFilterPanel(e) {
        if (e != null) {
            e.preventDefault();
        }

        mCalendarOptionsMenuIsOpen.set(this, false);

        if (!this.filterPanelInitialized) {
            mFilterPanelInitialized.set(this, true);
            mTimeoutSvc.get(this)( () => {
                mUserShowFilterPanel.set(this, true);
            }, 50);
        }
        else {
            mUserShowFilterPanel.set(this, true);
        }
    }

    exportCalendarToPdf(e) {
        if (e != null) {
            e.preventDefault();
        }

        mCalendarOptionsMenuIsOpen.set(this, false);

        let trackingScheduler = mScopeSvc.get(this).trackingScheduler;
        if (trackingScheduler != null) {
            let exportCompletePromise = trackingScheduler.saveAsPDF();

            exportCompletePromise.then(
                () => {
                    mLogSvc.get(this).info("Calendar PDF Export Completed.");
                },
                () => {
                    mLogSvc.get(this).error("Calendar PDF Export Failed.");
                }
            );
        }
    }

    showEventInfoPanel() {
        mEventInfoPanelIsVisible.set(this, true);
    }

    updateSelectedEvent(evt) {
        if (evt != null) {
            let currentEvent = evt;
            if (currentEvent.id === this.currentEventId) {
                this.showEventInfoPanel();
            }
            else if (Number.isInteger(currentEvent.id)) {
                mCurrentEventId.set(this, evt.id);
            }
        }

    }

    _setPageUrlImpl(calDate, cView, replaceCurrent) {

        let calTargetDate = null;
        if (angular.isDate(calDate)) {
            calTargetDate = calDate;
        }
        else {
            calTargetDate = mCalendarTargetDate.get(this);
        }

        if (calTargetDate == null) {
            mLogSvc.get(this).error("Cannot set page url with invalid date parameter.");
            return;
        }

        let calView = null;
        if (angular.isString(cView)) {
            calView = cView;
        }
        else {
            calView = mCurrentView.get(this);
        }

        if (calView == null) {
            mLogSvc.get(this).error("Cannot set page url with invalid view parameter");
            return;
        }

        let replaceCurrentUrl = (replaceCurrent === true);

        mUIRouterStateSvc.get(this).go("trackingCalendarView", {
            year: calTargetDate.getFullYear(),
            month: calTargetDate.getMonth() + 1,
            day: calTargetDate.getDate(),
            calendarViewType: calView
        }, {
            location: (replaceCurrentUrl ? "replace" : true) /* (Static-typed programmer cringes). */
        });

    }

    setPageUrl(calDate, cView) {
        this._setPageUrlImpl(calDate, cView, false);
    }

    replacePageUrl(calDate, cView) {
        this._setPageUrlImpl(calDate, cView, true);
    }

    setCurrentPageTitle() {
        let currentPageTitle = buildPageTitle(mCurrentView.get(this), mCalendarTargetDate.get(this), mFilterSvc.get(this)("date"));
        mDocumentSvc.get(this).attr("title", currentPageTitle);
    }

    onSchedulerNavigate(e) {

        mLogSvc.get(this).debug("Starting Navigate Event Handler");
        mLogSvc.get(this).debug(e);
        mCalendarTargetDate.set(this, e.date);
        if (e.action === "changeView") {
            let mappedViewName = viewTitleToViewNameMap.get(e.view);
            if (mappedViewName != null) {
                mCurrentView.set(this, mappedViewName);
            }
            else {
                mCurrentView.set(this, CalendarViews.Agenda);
            }
        }
        //$log.debug("Middle of Navigate Event Handler");
        this.setCurrentPageTitle();

        this.setPageUrl(mCalendarTargetDate.get(this), mCurrentView.get(this));
        mUIRouterPreviousStateSvc.get(this).forget(TrackingCalendarViewModuleConstants.prevStateLabel);
    }

    navigateToEventViewer(eventId) {
        mTimeoutSvc.get(this)(() => {
            mUIRouterStateSvc.get(this).go(
                TrackingEventEditorModuleConstants.rootStateName,
                { eventId: eventId },
                { location: true }
            );
        }, 200);
    }

    applyTrackingCalendarFilter() {
        mLogSvc.get(this).debug("Applying Event Filter.");
        let eventFilterStore = mEventFilterStoreSvc.get(this);
        const globalViewSvc = mCalendarGlobalViewSvc.get(this);

        let trackingCalendarFilter = eventFilterStore.calendarEventFilter;
        const currentGlobalView = globalViewSvc.getCurrentView();
        let trackingCalendarFilterForSource = trackingCalendarFilter.dataSourceFilter(currentGlobalView.rule);
        mScopeSvc.get(this).trackingScheduler.dataSource.filter(trackingCalendarFilterForSource);
        eventFilterStore.saveCalendarEventFilter();

        this._notifyFilterApplied({ calendarEventFilter: trackingCalendarFilter});

        mLogSvc.get(this).debug("Event Filter Applied and Saved");
    }

    _notifyFilterApplied({calendarEventFilter}) {
        Assert.StrictAssert(
            (calendarEventFilter != null) && (typeof calendarEventFilter.isEmpty === "function"),
            "Calendar View.Notify Filter Applied: Invalid calendar event filter argument."
        );

        mRootScopeSvc.get(this).$broadcast("tb-calendar-filter-applied",
            { filterIsActive: !calendarEventFilter.isEmpty() }
        );
    }

    applyTrackingCalendarOptions() {
        mLogSvc.get(this).debug("Calendar Options Applied. Agenda Days To Show Pref:");
        mLogSvc.get(this).debug(mTrackingCalendarOptionsSvc.get(this).AgendaDaysToShowPref);

        mWindowSvc.get(this).location.reload();
    }

    handleDirectEventLink(directLinkEventId) {
        this.updateSelectedEvent({ id: directLinkEventId });

        mTimeoutSvc.get(this)(() => {
            let scrollToId = mCalendarEventIdFormatFilter.get(this)(directLinkEventId);
            mLogSvc.get(this).debug(`Scrolling to '${scrollToId}'`);
            mAnchorScrollSvc.get(this)(scrollToId);
        }, 1000);
    }

    isAllDayEventContinuedFromPrevDay(schedulerEvent) {
        if (schedulerEvent == null) { return false; }

        if (!(schedulerEvent.isMultiDay())) { return false; }

        let viewCurrentCalendarStartDate = moment(mCalendarTargetDate.get(this)).tz(mCurrentTimeZone.get(this));

        let eventStartTimezone = schedulerEvent.startTimezone;
        if (eventStartTimezone == null || eventStartTimezone === "") {
            mLogSvc.get(this).warn("In isAllDayEventContinuedFromPrevDay: Got blank timezone from event. Using currently configured time zone instead.");
            eventStartTimezone = mCurrentTimeZone.get(this);
        }

        let eventStart = moment(schedulerEvent.start).tz(eventStartTimezone);

        if (isCurrentView(CalendarViews.Week, mCurrentView.get(this))) {
            viewCurrentCalendarStartDate.startOf("week");
        }
        else {
            viewCurrentCalendarStartDate.startOf("day");
        }

        let allDayEventStartingBeforeViewStartDate = eventStart.isBefore(viewCurrentCalendarStartDate);

        return allDayEventStartingBeforeViewStartDate;
    }
}

TrackingCalendarViewController.$inject = [
    "$scope",
    "$rootScope",
    "$timeout",
    "$log",
    "$anchorScroll",
    "$filter",
    "$document",
    "$window",
    "$templateRequest",
    "$state",
    "$transitions",
    "$transition$",
    "$previousState",
    "calendarParams",
    "TrackingCalendarData",
    "CurrentTimeZone",
    "UserInfoService",
    "TrackingCalendarOptions",
    "EventFilterStore",
    "tbCalendarEventIdFormatFilter",
    "CalendarGlobalViewService"
];

TrackingCalendarViewController.dependencies = [
    ngSanitize,
    ngAnimate,
    ngAria,
    uiRouter,
    uiRouterPreviousStateModule,
    uiBootstrap,
    kendoDirectivesModule,
    CurrentTimeZoneServiceModule,
    TrackingCalendarDataServiceModule,
    TrackingCalendarOptionsServiceModule,
    CalendarFilterPanelComponentModule,
    CalendarOptionsPanelComponentModule,
    CalendarEventInfoPanelComponentModule,
    TrackingCalendarEventComponentsModule,
    TimeZoneControlPanelComponentModule,
    UserInfoServiceModule,
    EventFilterStoreServiceModule,
    CalendarEventIdFormatFilterModule,
    CalendarGlobalViewServiceModule
];

TrackingCalendarViewController.template = require("./TrackingCalendarView.tpl.html");

const schedulerEventTemplate = require("./TrackingCalendarViewEvent.tpl.html");
TrackingCalendarViewController.childTemplates = Object.freeze({
    schedulerEventTemplate: schedulerEventTemplate
});
TrackingCalendarViewController.controllerAs = "calendarView";

export default TrackingCalendarViewController;



//////////////////
// WEBPACK FOOTER
// ./src/views/tracking-calendar-view/TrackingCalendarViewController.js
// module id = NIGS
// module chunks = 1